"use strict";
/**
 * Plan Document Types
 *
 * Type definitions for execution plans in the document-based planning system.
 * Plans are stored as AeroDocument with documentType: 'plan' in the documents table.
 *
 * Plans support:
 * - Iterative collaborative editing with AI
 * - Multi-document task targeting
 * - Step-by-step or automatic execution
 * - Conflict detection and recovery
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExecutionMode = void 0;
exports.describePlanStatus = describePlanStatus;
exports.getPlanStatusVariant = getPlanStatusVariant;
exports.calculatePlanProgress = calculatePlanProgress;
exports.canMarkReady = canMarkReady;
exports.canExecutePlan = canExecutePlan;
exports.validateTaskDependencies = validateTaskDependencies;
exports.createPlanTask = createPlanTask;
// ===== Execution Types =====
/**
 * Execution mode determines how the AI processes tasks
 */
var ExecutionMode;
(function (ExecutionMode) {
    /** Show what would happen, don't execute */
    ExecutionMode["PREVIEW"] = "preview";
    /** Execute one task, wait for user confirmation */
    ExecutionMode["STEP_BY_STEP"] = "step";
    /** Execute all tasks without pausing */
    ExecutionMode["AUTOMATIC"] = "automatic";
})(ExecutionMode || (exports.ExecutionMode = ExecutionMode = {}));
// ===== Helper Functions =====
/**
 * Get human-readable description of plan status
 */
function describePlanStatus(status) {
    switch (status) {
        case 'draft':
            return 'Draft - Add tasks and mark ready';
        case 'ready':
            return 'Ready - Click Execute to start';
        case 'executing':
            return 'Executing...';
        case 'paused':
            return 'Paused - Resume or cancel';
        case 'completed':
            return 'Completed';
        case 'partial':
            return 'Partially completed - Some tasks failed';
        case 'cancelled':
            return 'Cancelled';
        default:
            return 'Unknown status';
    }
}
/**
 * Get status badge color/variant
 */
function getPlanStatusVariant(status) {
    switch (status) {
        case 'draft':
            return 'secondary';
        case 'ready':
            return 'default';
        case 'executing':
            return 'warning';
        case 'paused':
            return 'warning';
        case 'completed':
            return 'success';
        case 'partial':
            return 'destructive';
        case 'cancelled':
            return 'outline';
        default:
            return 'default';
    }
}
/**
 * Calculate plan progress percentage
 */
function calculatePlanProgress(tasks) {
    if (tasks.length === 0)
        return 0;
    const completedTasks = tasks.filter(t => t.status === 'completed' || t.status === 'skipped').length;
    return Math.round((completedTasks / tasks.length) * 100);
}
/**
 * Check if plan can transition to ready state
 */
function canMarkReady(tasks) {
    if (tasks.length === 0) {
        return { canMark: false, reason: 'Plan has no tasks' };
    }
    const pendingTasks = tasks.filter(t => t.status === 'pending');
    if (pendingTasks.length === 0) {
        return { canMark: false, reason: 'All tasks already completed or skipped' };
    }
    return { canMark: true };
}
/**
 * Check if plan can be executed
 */
function canExecutePlan(status) {
    switch (status) {
        case 'ready':
            return { canExecute: true };
        case 'draft':
            return { canExecute: false, reason: 'Plan must be marked as ready first' };
        case 'executing':
            return { canExecute: false, reason: 'Plan is already executing' };
        case 'completed':
            return { canExecute: false, reason: 'Plan has already completed' };
        case 'paused':
            return { canExecute: true }; // Can resume
        case 'partial':
            return { canExecute: false, reason: 'Edit the plan to fix failed tasks first' };
        case 'cancelled':
            return { canExecute: false, reason: 'Plan was cancelled' };
        default:
            return { canExecute: false, reason: 'Unknown status' };
    }
}
/**
 * Validate task dependencies (no circular refs, deps exist, order is valid)
 */
function validateTaskDependencies(tasks) {
    const errors = [];
    const taskIds = new Set(tasks.map(t => t.id));
    const taskOrderMap = new Map(tasks.map(t => [t.id, t.order]));
    for (const task of tasks) {
        if (task.dependencies) {
            for (const depId of task.dependencies) {
                // Check dependency exists
                if (!taskIds.has(depId)) {
                    errors.push(`Task "${task.description}" depends on non-existent task ${depId}`);
                    continue;
                }
                // Check dependency comes before this task
                const depOrder = taskOrderMap.get(depId);
                if (depOrder !== undefined && depOrder >= task.order) {
                    const depTask = tasks.find(t => t.id === depId);
                    errors.push(`Task "${task.description}" depends on "${depTask?.description}" which comes after it`);
                }
            }
        }
    }
    return { valid: errors.length === 0, errors };
}
/**
 * Create a new empty task
 */
function createPlanTask(description, order, targetDocumentNumber) {
    return {
        id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        description,
        targetDocumentNumber,
        status: 'pending',
        order,
    };
}
//# sourceMappingURL=plan-types.js.map